<template>
    <BaseDropdownMenu :sections="menuSections" align="start" side="top" :aria-label="t('navigation.legacyAccount.settings')" @select="handleSelect">
        <template #trigger>
            <BaseTooltip side="right" :disabled="showLabel">
                <template #trigger>
                    <BaseButton
                        data-testid="settings-button"
                        type="button"
                        theme="tertiary"
                        size="sm"
                        :icon-only="!showLabel"
                        :aria-label="t('navigation.legacyAccount.settings')"
                        class="flex items-center">
                        <i class="bi bi-gear nav-icon-legacy leading-none" aria-hidden="true" />
                        <span v-if="showLabel" class="ml-3 text-left">{{ t("navigation.legacyAccount.settings") }}</span>
                    </BaseButton>
                </template>
                <template #content>{{ t("navigation.legacyAccount.settings") }}</template>
            </BaseTooltip>
        </template>
    </BaseDropdownMenu>
    <form ref="logoutForm" method="POST" :action="logoutUrl" style="display: none">
        <input type="hidden" name="csrfmiddlewaretoken" :value="getCookie('csrftoken')" />
    </form>
</template>

<script setup lang="ts">
import { getCookie } from "/js/utils.js";
import type { GlobalProperties } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import AlertCircle from "~vue/icons/AlertCircle.vue";
import Logout from "~vue/icons/Logout.vue";
import { isInIFrame } from "~vue/utils";
import { discardBrowserHistory } from "~vue/utils/browserHistory";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

type SettingsMenuProps = {
    showLabel?: boolean;
};

const { showLabel = true } = defineProps<SettingsMenuProps>();

const globalProperties = inject<GlobalProperties>("globalProperties");

const feedbackUrl = isInIFrame() ? "/microsoft-teams/report-issue/" : (globalProperties?.$feedbackMailto ?? "");
const logoutUrl = isInIFrame() ? "/accounts/logout/?next=/microsoft-teams/login/" : "/accounts/logout/";

const menuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "settings",
        items: [
            {
                id: "profile",
                type: "inertia-link",
                href: "/accounts/user-profile",
                label: t("navigation.panes.profile"),
                icon: "bi bi-person-circle",
                testId: "profile",
                itemClass: "md:hidden",
            },
            {
                id: "preferences",
                type: "inertia-link",
                href: "/accounts/user-preferences",
                label: t("navigation.account.preferences"),
                icon: "bi bi-sliders",
                testId: "preferences",
            },
            { id: "feedback", type: "anchor", href: feedbackUrl, label: t("navigation.legacyAccount.reportIssue"), icon: AlertCircle, testId: "feedback" },
            { id: "logout", type: "button", label: t("navigation.legacyAccount.logout"), icon: Logout, testId: "logout" },
        ],
    },
]);

const logoutForm = useTemplateRef<HTMLFormElement>("logoutForm");

const handleSelect = (item: DropdownMenuItem) => {
    if (item.id === "feedback") {
        logUserInteraction("navigation_report_issue_clicked");
    }
    if (item.id === "logout") {
        discardBrowserHistory();
        logoutForm.value?.submit();
    }
};
</script>
